import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getDistricts, getCities, getRegions, createDistrict, updateDistrict, deleteDistrict } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function DistrictsPage() {
  const { t, language } = useLanguage();
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCity, setFilterCity] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingDistrict, setEditingDistrict] = useState(null);
  const [deletingDistrict, setDeletingDistrict] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    city_id: '',
    is_active: true,
  });

  const fetchData = async () => {
    try {
      const [districtsRes, citiesRes, regionsRes] = await Promise.all([
        getDistricts(),
        getCities(),
        getRegions()
      ]);
      setDistricts(districtsRes.data);
      setCities(citiesRes.data);
      setRegions(regionsRes.data);
    } catch (error) {
      toast.error(t('error.general'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getCityName = (cityId) => {
    const city = cities.find(c => c.id === cityId);
    return language === 'ar' ? city?.name_ar : (city?.name_en || city?.name_ar);
  };

  const handleOpenDialog = (district = null) => {
    if (district) {
      setEditingDistrict(district);
      setFormData({
        name_ar: district.name_ar || '',
        name_en: district.name_en || '',
        city_id: district.city_id || '',
        is_active: district.is_active !== false,
      });
    } else {
      setEditingDistrict(null);
      setFormData({ name_ar: '', name_en: '', city_id: '', is_active: true });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name_ar.trim() || !formData.city_id) {
      toast.error(t('validation.required'));
      return;
    }
    
    setSaving(true);
    try {
      if (editingDistrict) {
        await updateDistrict(editingDistrict.id, formData);
        toast.success(t('success.updated'));
      } else {
        await createDistrict(formData);
        toast.success(t('success.created'));
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingDistrict) return;
    
    try {
      await deleteDistrict(deletingDistrict.id);
      toast.success(t('success.deleted'));
      setDeleteDialogOpen(false);
      setDeletingDistrict(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    }
  };

  const filteredDistricts = districts.filter(district => {
    const matchesSearch = district.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      district.name_en?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCity = filterCity === 'all' || district.city_id === filterCity;
    return matchesSearch && matchesCity;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A4C3A]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="districts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder={t('search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ps-10"
              data-testid="districts-search"
            />
          </div>
          <Select value={filterCity} onValueChange={setFilterCity}>
            <SelectTrigger className="w-full sm:w-48" data-testid="districts-filter-city">
              <SelectValue placeholder={t('select_city')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all')}</SelectItem>
              {cities.map((city) => (
                <SelectItem key={city.id} value={city.id}>
                  {language === 'ar' ? city.name_ar : (city.name_en || city.name_ar)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-[#0A4C3A] hover:bg-[#073B2D]"
          data-testid="add-district-button"
        >
          <Plus className="w-4 h-4 me-2" />
          {t('add_district')}
        </Button>
      </div>

      {/* Table */}
      <Card className="bg-white border border-gray-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-medium">{t('district_name_ar')}</TableHead>
                <TableHead className="font-medium">{t('district_name_en')}</TableHead>
                <TableHead className="font-medium">{t('city')}</TableHead>
                <TableHead className="font-medium">{t('status')}</TableHead>
                <TableHead className="font-medium text-center">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDistricts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    {t('no_data')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredDistricts.slice(0, 50).map((district) => (
                  <TableRow key={district.id} data-testid={`district-row-${district.id}`}>
                    <TableCell className="font-medium">{district.name_ar}</TableCell>
                    <TableCell>{district.name_en || '-'}</TableCell>
                    <TableCell>{getCityName(district.city_id)}</TableCell>
                    <TableCell>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        district.is_active !== false
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {district.is_active !== false ? t('active') : t('inactive')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(district)}
                          data-testid={`edit-district-${district.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setDeletingDistrict(district);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-district-${district.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingDistrict ? t('edit_district') : t('add_district')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="city_id">{t('city')} *</Label>
              <Select 
                value={formData.city_id} 
                onValueChange={(value) => setFormData({ ...formData, city_id: value })}
              >
                <SelectTrigger data-testid="district-city-select">
                  <SelectValue placeholder={t('select_city')} />
                </SelectTrigger>
                <SelectContent>
                  {cities.map((city) => (
                    <SelectItem key={city.id} value={city.id}>
                      {language === 'ar' ? city.name_ar : (city.name_en || city.name_ar)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name_ar">{t('district_name_ar')} *</Label>
              <Input
                id="name_ar"
                value={formData.name_ar}
                onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                data-testid="district-name-ar"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name_en">{t('district_name_en')}</Label>
              <Input
                id="name_en"
                value={formData.name_en}
                onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                dir="ltr"
                data-testid="district-name-en"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="is_active">{t('active')}</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="district-is-active"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#0A4C3A] hover:bg-[#073B2D]"
              data-testid="save-district-button"
            >
              {saving && <Loader2 className="w-4 h-4 me-2 animate-spin" />}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirm_delete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingDistrict?.name_ar}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-district"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
